// src/components/Admin/JobsTab.js
import { useState, useEffect, useCallback, useMemo } from "react";
import { MapPin, Briefcase } from "lucide-react";
import { fetchAllJobs } from "../services/adminJobs";
import { serviceCategories } from "../data/mockData";

// categoryId -> name lookup
const CATEGORY_NAME = serviceCategories.reduce((acc, c) => {
  acc[c.id] = c.name;
  return acc;
}, {});
const categoryName = (id) => CATEGORY_NAME[id] || id || "—";

const STATUS_OPTIONS = [
  "all",
  "searching",
  "dispatched",
  "assigned",
  "on_the_way",
  "arrived",
  "completed",
  "cancelled",
  "no_tasker",
];

const STATUS_STYLES = {
  draft: "bg-gray-100 text-gray-500",
  searching: "bg-blue-100 text-blue-700",
  dispatched: "bg-indigo-100 text-indigo-700",
  assigned: "bg-orange-100 text-orange-700",
  on_the_way: "bg-amber-100 text-amber-700",
  arrived: "bg-teal-100 text-teal-700",
  completed: "bg-green-100 text-green-700",
  cancelled: "bg-red-100 text-red-700",
  no_tasker: "bg-rose-100 text-rose-700",
};

function StatusBadge({ status }) {
  const s = status || "draft";
  const style = STATUS_STYLES[s] || "bg-gray-100 text-gray-600";
  return (
    <span className={`text-xs px-2.5 py-0.5 rounded-full ${style}`}>
      {s.replace(/_/g, " ")}
    </span>
  );
}

function formatDate(ts) {
  if (!ts) return "—";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString();
}

const labelize = (s) => (s === "all" ? "All" : s.replace(/_/g, " "));

function Dropdown({ label, value, options, onChange, renderLabel = labelize }) {
  return (
    <label className="flex flex-col text-xs text-gray-500 gap-1">
      {label}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="min-w-36 px-3 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#006C35] capitalize"
      >
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {renderLabel(opt)}
          </option>
        ))}
      </select>
    </label>
  );
}

export default function JobsTab() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setJobs(await fetchAllJobs());
    } catch (err) {
      console.error("fetchAllJobs error:", err);
      setError("Could not load jobs.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Build dropdown option lists from the actual data
  const categoryOptions = useMemo(() => {
    const ids = Array.from(
      new Set(jobs.map((j) => j.categoryId).filter(Boolean))
    );
    ids.sort((a, b) => categoryName(a).localeCompare(categoryName(b)));
    return ["all", ...ids];
  }, [jobs]);



  const shown = jobs.filter((j) => {
    const statusOk =
      statusFilter === "all" || (j.status || "draft") === statusFilter;
    const catOk = categoryFilter === "all" || j.categoryId === categoryFilter;
    return statusOk && catOk;
  });

  const feeRevenue = jobs
    .filter((j) => j.status === "completed")
    .reduce((sum, j) => sum + (Number(j.platformFee) || 0), 0);

  return (
    <div>
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Jobs</h2>
          <p className="text-gray-500 text-sm">
            {shown.length} shown · {jobs.length} total ·{" "}
            {feeRevenue.toLocaleString()} SAR fees collected
          </p>
        </div>

        <div className="flex items-end gap-3 flex-wrap">
          <Dropdown
            label="Status"
            value={statusFilter}
            options={STATUS_OPTIONS}
            onChange={setStatusFilter}
          />
          <Dropdown
            label="Category"
            value={categoryFilter}
            options={categoryOptions}
            onChange={setCategoryFilter}
            renderLabel={(o) => (o === "all" ? "All" : categoryName(o))}
          />
        </div>
      </div>

      {loading && <div className="text-gray-500 py-8">Loading jobs...</div>}
      {error && <div className="text-red-600 py-8">{error}</div>}

      {!loading && !error && shown.length === 0 && (
        <div className="flex flex-col items-center justify-center text-gray-400 py-16">
          <Briefcase size={40} className="mb-3" />
          <p>No jobs match these filters.</p>
        </div>
      )}

      {!loading && !error && shown.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="text-left px-4 py-3 font-medium">Booking</th>
                  <th className="text-left px-4 py-3 font-medium">Category</th>
                  <th className="text-left px-4 py-3 font-medium">Location</th>
                  <th className="text-left px-4 py-3 font-medium">Status</th>
                  <th className="text-left px-4 py-3 font-medium">Tasker</th>
                  <th className="text-left px-4 py-3 font-medium">Fee</th>
                  <th className="text-left px-4 py-3 font-medium">Payment</th>
                  <th className="text-left px-4 py-3 font-medium">Created</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {shown.map((j) => (
                  <tr key={j.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="text-gray-800">{j.bookingId || j.id}</div>
                      <div className="text-xs text-gray-400">{j.id}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {categoryName(j.categoryId)}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      <span className="inline-flex items-center gap-1">
                        <MapPin size={14} className="text-gray-400" />
                        {j.location?.address || "—"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={j.status} />
                    </td>
                    <td className="px-4 py-3">
                      {j.taskerId ? (
                        <span className="text-xs text-gray-500">
                          {j.taskerId}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">Unassigned</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {j.platformFee ? `${j.platformFee} SAR` : "—"}
                    </td>
                    <td className="px-4 py-3 text-gray-600 capitalize">
                      {j.paymentStatus || "—"}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {formatDate(j.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}